import { Area, LinePath } from '@visx/shape';
import { useTranslation } from 'react-i18next';
import type { SecondBucket } from '../perSecond';
import { axisMax, LegendItem, TimeChart, type TimeAxis } from './TimeChart';
import { formatMs, latest } from './values';

const DOT = 1.5;

/**
 * Ping per second: the median as a line, the 95th percentile as a light band above it, the maximum as
 * dots. The axis follows the 95th percentile, so one late echo cannot flatten the median; a higher
 * maximum sits at the top edge.
 */
export const PingChart = ({ slots, axis }: { slots: SecondBucket[]; axis: TimeAxis }) => {
    const { t, i18n } = useTranslation();
    const last = latest(slots, (b) => b.rttP50 !== null);
    const none = t('TestSuite.Line.NoValue');
    const yMax = axisMax(
        slots.map((b) => b.rttP95 ?? 0),
        10
    );
    const mid = (b: SecondBucket) => b.second + axis.slot / 2;

    return (
        <TimeChart
            title={t('TestSuite.Line.Chart.Ping.Title')}
            ariaLabel={t('TestSuite.Line.Chart.Ping.Aria', { p50: formatMs(last?.rttP50, none, i18n.language), p95: formatMs(last?.rttP95, none, i18n.language), max: formatMs(last?.rttMax, none, i18n.language) })}
            legend={
                <>
                    <LegendItem swatch="bg-primary" label={t('TestSuite.Line.Legend.Median')} />
                    <LegendItem swatch="bg-primary opacity-25" label={t('TestSuite.Line.Legend.P95')} />
                    <LegendItem swatch="bg-warning rounded-full" label={t('TestSuite.Line.Legend.Max')} />
                </>
            }
            axis={axis}
            yMax={yMax}
            unit="ms"
        >
            {({ x, y }) => (
                <>
                    <Area
                        data={slots}
                        defined={(b) => b.rttP50 !== null && b.rttP95 !== null}
                        x={(b) => x(mid(b))}
                        y0={(b) => y(b.rttP50 ?? 0)}
                        y1={(b) => y(b.rttP95 ?? 0)}
                        className="fill-primary opacity-25"
                    />
                    <LinePath data={slots} defined={(b) => b.rttP50 !== null} x={(b) => x(mid(b))} y={(b) => y(b.rttP50 ?? 0)} strokeWidth={1.5} className="stroke-primary fill-none" />
                    {/* One path for all dots: hundreds of <circle> elements cost a render every second. */}
                    <path
                        d={slots
                            .filter((b) => b.rttMax !== null)
                            .map((b) => `M${x(mid(b)) - DOT},${y(b.rttMax ?? 0)}a${DOT},${DOT} 0 1,0 ${2 * DOT},0a${DOT},${DOT} 0 1,0 ${-2 * DOT},0`)
                            .join('')}
                        className="fill-warning"
                    />
                </>
            )}
        </TimeChart>
    );
};
